import { Star } from 'lucide-react';
import { toast } from 'sonner';

import {
  findFavoriteForItem,
  type FavoriteItemType,
} from '@/features/favorites/api';
import {
  useAddFavorite,
  useFavorites,
  useRemoveFavorite,
} from '@/features/favorites/hooks';
import { cn } from '@/lib/utils';

type Props = {
  itemType: FavoriteItemType;
  itemId: string;
  itemName: string;
  className?: string;
  /** Optional compact icon-only control (default). */
  size?: 'sm' | 'md';
  testId?: string;
};

/**
 * Accessible star toggle for live Workspace / Project / Dashboard surfaces.
 * Filled state only after server confirmation (or rollback-safe optimistic remove).
 */
export function FavoriteStarButton({
  itemType,
  itemId,
  itemName,
  className,
  size = 'sm',
  testId,
}: Props) {
  const { data: favorites, isError } = useFavorites();
  const add = useAddFavorite();
  const remove = useRemoveFavorite();
  const existing = findFavoriteForItem(favorites, itemType, itemId);
  const favorited = Boolean(existing);
  const pending = add.isPending || remove.isPending;
  const iconClass = size === 'md' ? 'h-5 w-5' : 'h-4 w-4';

  const label = favorited
    ? `Remove ${itemName} from favorites`
    : `Add ${itemName} to favorites`;

  return (
    <button
      type="button"
      data-testid={testId ?? `favorite-star-${itemType}-${itemId}`}
      aria-label={label}
      aria-pressed={favorited}
      title={label}
      disabled={pending || isError}
      className={cn(
        'inline-flex items-center justify-center rounded-lg p-1.5 transition',
        favorited
          ? 'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950/40'
          : 'text-slate-400 hover:bg-slate-100 hover:text-amber-500 dark:hover:bg-slate-800',
        pending && 'opacity-60',
        className,
      )}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (pending) return;
        if (existing) {
          remove.mutate({ favoriteId: existing.favoriteId });
          return;
        }
        add.mutate(
          { itemType, itemId },
          {
            onError: () => {
              /* toast from hook */
            },
          },
        );
      }}
    >
      <Star
        className={cn(iconClass, favorited && 'fill-current')}
        aria-hidden
      />
    </button>
  );
}

/** Menu-row style add/remove for overflow menus (workspace/project …). */
export function useFavoriteMenuAction(
  itemType: FavoriteItemType,
  itemId: string,
  itemName: string,
) {
  const { data: favorites } = useFavorites();
  const add = useAddFavorite();
  const remove = useRemoveFavorite();
  const existing = findFavoriteForItem(favorites, itemType, itemId);
  const pending = add.isPending || remove.isPending;

  return {
    favorited: Boolean(existing),
    pending,
    label: existing
      ? `Remove ${itemName} from favorites`
      : `Add ${itemName} to favorites`,
    shortLabel: existing ? 'Remove from favorites' : 'Add to favorites',
    run: () => {
      if (pending) return;
      if (existing) {
        remove.mutate({ favoriteId: existing.favoriteId });
        return;
      }
      add.mutate(
        { itemType, itemId },
        {
          onError: () => toast.error('Could not update favorites'),
        },
      );
    },
  };
}
